<template>
  <div class="sidebar-container" :class="{ 'sidebar-collapsed': !sidebarExpanded }">
    <div class="sidebar">
      <div class="sidebar--logo">
        <template v-if="sidebarExpanded"><Logo /></template>
        <template v-if="!sidebarExpanded"><LogoAbridged /></template>

        <Button @click="toggleSidebar()" class="sidebar-toggle">
          <IconSidebarCollapse v-if="sidebarExpanded" />
          <IconSidebarExpand v-if="!sidebarExpanded" />
        </Button>
      </div>

      <ul class="sidebar--links">
        <!-- Overview -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'overview' }"
          data-step="0"
        >
          <RouterLink to="/">
            <IconOverview />
            <span class="sidebar-link-title">Overview</span>
          </RouterLink>
        </li>

        <!-- Events -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'events' }"
          data-step="4"
        >
          <RouterLink v-if="userStore.hasScope('events.show')" to="/events">
            <IconCalendar />
            <span class="sidebar-link-title">Events</span>
          </RouterLink>
          <Tooltip v-else position="right">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconCalendar />
                <span class="sidebar-link-title">Events</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Event Booking -->
        <FeatureFlag name="event_booking">
          <li
            v-if="!userStore.isOperationsUser"
            :class="{ 'sidebar-link--active': routeMenu === 'booking' }"
            data-step="7"
          >
            <RouterLink to="/events/purchasing">
              <IconBooking />
              <span class="sidebar-link-title">Booking</span>
            </RouterLink>
          </li>
        </FeatureFlag>

        <!-- Sensors -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'sensors' }"
          data-step="7"
        >
          <RouterLink v-if="userStore.hasScope('sensors.show')" to="/sensors">
            <IconSensors />
            <span class="sidebar-link-title">Sensors</span>
          </RouterLink>
          <Tooltip v-else position="right">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconSensors />
                <span class="sidebar-link-title">Sensors</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Live Monitor -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'live' }">
          <RouterLink to="/sensors/live">
            <IconLive />
            <span class="sidebar-link-title">Live Monitor</span>
          </RouterLink>
        </li>

        <!-- Users -->
        <li
          v-if="!userStore.isOperationsUser"
          :class="{ 'sidebar-link--active': routeMenu === 'users' }"
          data-step="6"
        >
          <RouterLink v-if="userStore.hasScope('users.show')" to="/users">
            <IconUsers />
            <span class="sidebar-link-title">Users</span>
          </RouterLink>
          <Tooltip v-else position="right">
            <template v-slot:text>
              <span class="sidebar-link--disabled">
                <IconUsers />
                <span class="sidebar-link-title">Users</span>
              </span>
            </template>
            <template v-slot:tooltip> You do not have access to view this page. </template>
          </Tooltip>
        </li>

        <!-- Accounts -->
        <li
          v-if="userStore.hasScope('accounts.show') && userStore.isStaff"
          class="with-submenu"
          :class="{ 'sidebar-link--active': (activeMenu || routeMenu) === 'accounts' }"
        >
          <div @click="setMenu('accounts')" class="sidebar-link--header">
            <IconAccounts />
            <span class="sidebar-link-title">Accounts</span>
            <IconCaretUp v-if="activeMenu === 'accounts'" class="sidebar-header-collapse" />
          </div>

          <div class="submenu">
            <ul>
              <li>
                <RouterLink to="/accounts">
                  <IconEdit />
                  <span class="sidebar-link-title">Manage Accounts</span>
                </RouterLink>
              </li>
              <li v-if="userStore.isStaff">
                <RouterLink to="/roles">
                  <IconEdit />
                  <span class="sidebar-link-title">Manage Roles</span>
                </RouterLink>
              </li>
            </ul>
          </div>
        </li>

        <!-- Admin -->
        <FeatureFlag name="client_profile">
          <li
            v-if="userStore.isClientAdmin"
            class="with-submenu"
            :class="{ 'sidebar-link--active': (activeMenu || routeMenu) === 'admin' }"
          >
            <div @click="setMenu('admin')" class="sidebar-link--header">
              <IconAdmin />
              <span class="sidebar-link-title">Admin</span>
              <IconCaretUp v-if="activeMenu === 'admin'" class="sidebar-header-collapse" />
            </div>

            <div class="submenu">
              <ul>
                <li>
                  <RouterLink :to="`/admin/profile/${userStore.userData?.account_id}`">
                    <IconProfile />
                    <span class="sidebar-link-title">Profile</span>
                  </RouterLink>
                </li>
                <li>
                  <RouterLink to="/admin/documents">
                    <IconDocument />
                    <span class="sidebar-link-title">Documents</span>
                  </RouterLink>
                </li>
              </ul>
            </div>
          </li>
        </FeatureFlag>
      </ul>

      <ul class="sidebar--links sidebar--links-extra">
        <!-- FAQ -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'faq' }" data-step="8">
          <RouterLink to="/faq">
            <IconFAQ />
            <span class="sidebar-link-title">FAQ</span>
          </RouterLink>
        </li>

        <!-- API -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'api' }">
          <RouterLink to="/api">
            <IconAPI />
            <span class="sidebar-link-title">API</span>
          </RouterLink>
        </li>

        <!-- Contact -->
        <li :class="{ 'sidebar-link--active': routeMenu === 'contact' }">
          <RouterLink to="/contact">
            <IconContact />
            <span class="sidebar-link-title">Contact</span>
          </RouterLink>
        </li>
      </ul>

      <UserMenu position="top">
        <template #button>
          <div class="sidebar--user-info" data-step="9">
            <div class="sidebar--user-info--logo">
              <img v-if="getAccountLogo != ''" :src="getAccountLogo" />
            </div>
            <div class="sidebar--user-info--content">
              <div class="sidebar--user-info--name">
                {{ userStore.userData?.name }}
              </div>
              <div class="sidebar--user-info--role">
                {{ userStore.userData?.role_name }}
              </div>
            </div>
            <IconCaretDown />
          </div>
        </template>
      </UserMenu>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import { Button } from 'primevue';
import UserMenu from '@/components/global/UserMenu.vue';
import Tooltip from './Tooltip.vue';

import Logo from '@/icons/Logo.svg?component';
import LogoAbridged from '@/icons/LogoAbridged.svg?component';

import IconSidebarCollapse from '@/icons/sidebar/IconSidebarCollapse.svg?component';
import IconSidebarExpand from '@/icons/sidebar/IconSidebarExpand.svg?component';
import IconAPI from '@/icons/sidebar/IconAPI.svg?component';
import IconAdmin from '@/icons/sidebar/IconAdmin.svg?component';
import IconAccounts from '@/icons/sidebar/IconAccounts.svg?component';
import IconBooking from '@/icons/sidebar/IconBooking.svg?component';
import IconCalendar from '@/icons/sidebar/IconCalendar.svg?component';
import IconCaretUp from '@/icons/arrows/IconCaretUp.svg?component';
import IconCaretDown from '@/icons/arrows/IconCaretDown.svg?component';
import IconContact from '@/icons/sidebar/IconContact.svg?component';
import IconDocument from '@/icons/IconDocument.svg?component';
import IconEdit from '@/icons/IconEdit.svg?component';
import IconFAQ from '@/icons/sidebar/IconFAQ.svg?component';
import IconLive from '@/icons/sidebar/IconLive.svg?component';
import IconOverview from '@/icons/sidebar/IconOverview.svg?component';
import IconProfile from '@/icons/UserProfile.svg?component';
import IconSensors from '@/icons/sidebar/IconSensors.svg?component';
import IconUsers from '@/icons/sidebar/IconUsers.svg?component';

import { useUserStore } from '@/stores/user';
import FeatureFlag from './FeatureFlag.vue';
const userStore = useUserStore();

const route = useRoute();
const activeMenu = ref('');
const routeMenu = ref('');

const setMenu = (menu: string) => {
  if (activeMenu.value === menu) {
    activeMenu.value = '';
  } else {
    activeMenu.value = menu;
  }
};

if (route.path === '/') {
  routeMenu.value = 'overview';
} else if (route.path.includes('purchasing')) {
  routeMenu.value = 'booking';
} else if (route.path.includes('events')) {
  routeMenu.value = 'events';
} else if (route.path.includes('live')) {
  routeMenu.value = 'live';
} else if (route.path.includes('sensors')) {
  routeMenu.value = 'sensors';
} else if (route.path.includes('users')) {
  routeMenu.value = 'users';
} else if (route.path.includes('admin')) {
  routeMenu.value = 'admin';
} else if (route.path.includes('accounts') || route.path.includes('roles')) {
  routeMenu.value = 'accounts';
} else if (route.path.includes('faq')) {
  routeMenu.value = 'faq';
} else if (route.path.includes('api')) {
  routeMenu.value = 'api';
} else if (route.path.includes('contact')) {
  routeMenu.value = 'contact';
}

const sidebarExpanded = ref(true);
const toggleSidebar = () => {
  sidebarExpanded.value = !sidebarExpanded.value;
};

const getAccountLogo = computed(() => {
  if (userStore.userData?.account_logo) {
    return `/media/${userStore.userData.account_logo}`;
  }
  return '';
});
</script>

<style lang="scss">
.sidebar-container {
  flex-grow: 0;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  transition: width 0.3s ease-in-out;
  width: 240px;
  will-change: width;
  z-index: 10;

  .p-button.sidebar-toggle {
    @include mixins.flex-center($horiz: flex-end);
    @include sizing.padding('xxxs');
    background: variables.$vantage-base-primitives-color-background-200;
    border-radius: 0;
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
    color: variables.$vantage-base-primitives-color-tertiary-600;
    height: 30px;
    position: absolute;
    right: 0;
    top: variables.$vantage-base-sizing-lg;
    width: 30px;

    svg {
      height: 16px;
      margin: 0;
      width: 16px;
    }
  }

  &.sidebar-collapsed {
    width: 70px;

    .p-button.sidebar-toggle {
      background: variables.$vantage-base-primitives-color-tertiary-600;
      border-radius: 0;
      border-top-right-radius: 50%;
      border-bottom-right-radius: 50%;
      color: variables.$vantage-base-primitives-color-background-100;
      justify-content: flex-start;
      right: -30px;
    }

    .sidebar-link-title,
    .sidebar--user-info .sidebar--user-info--content {
      width: 0;
    }

    .menu-container svg {
      display: none;
    }
  }

  .sidebar {
    @include mixins.flex-center($horiz: flex-start);
    @include sizing.padding('xs');
    align-items: flex-start;
    background: variables.$vantage-base-primitives-color-tertiary-600;
    flex-direction: column;
    height: 100vh;
    position: sticky;
    top: 0;
    white-space: nowrap;

    &.sidebar-collapsed {
      width: 70px;
    }
  }

  .sidebar--logo {
    @include typography.font-display($weight: bold, $size: sm);
    @include sizing.padding('xs');
    color: variables.$vantage-base-primitives-color-background-100;
    flex-grow: 0;
    height: 120px;

    svg {
      height: auto;
      width: 100px;
    }
  }

  .sidebar--content {
    @include mixins.flex-center($horiz: flex-start, $wrap: true);
    height: 100%;
    margin-bottom: auto;
    overflow-x: visible;
    overflow-y: scroll;
    width: 100%;
  }
}

.sidebar--user-info {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxs);
  @include sizing.padding('xs', 0);
  cursor: pointer;
  flex-wrap: nowrap;
  font-size: 14px;
  margin-top: variables.$vantage-base-sizing-xs;
  width: 100%;

  .sidebar--user-info--logo {
    max-width: 46px;

    img {
      min-width: 46px;
      width: 100%;
    }
  }

  .sidebar--user-info--content {
    overflow: hidden;
    transition: width ease-in-out 200ms;
    width: 100%;
    will-change: width;
  }

  .sidebar--user-info--name {
    color: variables.$vantage-base-primitives-color-background-100;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: uppercase;
  }

  .sidebar--user-info--role {
    color: variables.$vantage-base-primitives-color-primary-100;
  }

  svg {
    color: variables.$vantage-base-primitives-color-background-100;
    flex-basis: 20px;
    flex-shrink: 0;
  }
}

.sidebar--links {
  list-style-type: none;
  margin-bottom: auto;
  width: 100%;

  svg {
    flex-shrink: 0;
  }

  > li {
    margin-bottom: variables.$vantage-base-sizing-xxs;
  }

  li {
    cursor: pointer;

    a,
    span.sidebar-link--disabled,
    .sidebar-link--header {
      @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxxs);
      @include typography.font-body($weight: medium, $size: md);
      @include sizing.padding('xxs', 'xs');
      border-radius: variables.$vantage-base-sizing-xxs;
      color: variables.$vantage-base-primitives-color-background-100;
      transition:
        background-color ease-in-out 200ms,
        color ease-in-out 200ms;
      will-change: background-color, color;
      width: 100%;
    }
  }

  li:hover {
    > a,
    > .sidebar-link--header {
      background: variables.$vantage-base-primitives-color-secondary-200;
      border-radius: variables.$vantage-base-sizing-xxs;
      color: variables.$vantage-base-primitives-color-tertiary-600;
    }
  }

  li.sidebar-link--active {
    > a,
    > .sidebar-link--header {
      background: variables.$vantage-base-primitives-color-secondary-400;
      border-radius: variables.$vantage-base-sizing-xxs;
      color: variables.$vantage-base-primitives-color-tertiary-600;
    }
  }

  li span.sidebar-link--disabled {
    cursor: not-allowed;
    opacity: 0.3;
  }

  .sidebar-link-title {
    overflow: hidden;
    transition: width ease-in-out 200ms;
    width: 100%;
    will-change: width;
  }

  .submenu {
    background-color: variables.$vantage-base-primitives-color-tertiary-700;
    border-bottom-left-radius: variables.$vantage-base-sizing-xxs;
    border-bottom-right-radius: variables.$vantage-base-sizing-xxs;
    display: grid;
    grid-template-rows: 0fr;
    transition: grid-template-rows 300ms ease;
    width: 100%;

    ul {
      list-style-type: none;
      overflow: hidden;
    }

    a {
      @include sizing.padding('xxs', 'xxs', 'xxs', 'lg');
    }
  }

  li.sidebar-link--active {
    .submenu {
      grid-template-rows: 1fr;
    }
  }

  .sidebar-header-collapse {
    margin-left: auto;
  }
}

.sidebar--links-extra {
  margin-bottom: 0;
  margin-top: auto;
}
</style>
